import { PageLoader } from '@/components/ui/loader';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, Copy, Globe, Mail, MessageCircle, Plus, Send, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Switch, Textarea } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import {
  KIND_COLOURS,
  KIND_LABELS,
  useChannels,
  useDeleteChannel,
  useSalesbots,
  useSaveChannel,
  REPLY_TARGETS,
  type Channel,
  type ChannelKind,
} from './api';

const KINDS: { kind: ChannelKind; blurb: string }[] = [
  { kind: 'WEB_CHAT', blurb: 'A chat bubble on your website. One line of code.' },
  { kind: 'TELEGRAM', blurb: 'Your Telegram bot. Paste the token from @BotFather.' },
  { kind: 'FACEBOOK', blurb: 'Messages to your Facebook Page, through a Meta app.' },
  { kind: 'INSTAGRAM', blurb: 'Instagram Direct, through the Page it is linked to.' },
  { kind: 'EMAIL', blurb: 'A shared mailbox — sales@, info@ — read and answered here.' },
  { kind: 'VIBER', blurb: 'Your Viber bot. Paste the token from the Viber Partners site.' },
  { kind: 'SMS', blurb: 'Two-way text messages through a Twilio number or sender ID.' },
];

/** Settings for the mail services people actually use, so nobody looks up ports. */
const MAIL_PRESETS: Record<string, { imapHost: string; imapPort: number; smtpHost: string; smtpPort: number; note: string }> = {
  gmail: {
    imapHost: 'imap.gmail.com',
    imapPort: 993,
    smtpHost: 'smtp.gmail.com',
    smtpPort: 465,
    note: 'Gmail / Google Workspace: turn on 2-step verification and use an App password, not your normal one.',
  },
  outlook: {
    imapHost: 'outlook.office365.com',
    imapPort: 993,
    smtpHost: 'smtp.office365.com',
    smtpPort: 587,
    note: 'Microsoft 365: IMAP and authenticated SMTP must be enabled for the mailbox by your admin.',
  },
  zoho: {
    imapHost: 'imappro.zoho.com',
    imapPort: 993,
    smtpHost: 'smtppro.zoho.com',
    smtpPort: 465,
    note: 'Zoho Mail: enable IMAP access in Zoho Mail settings.',
  },
  other: { imapHost: '', imapPort: 993, smtpHost: '', smtpPort: 465, note: 'Your provider lists these as IMAP and SMTP settings.' },
};

/** Connecting the messengers whose conversations land in the inbox. */
export function ChannelsPage() {
  const { data: channels = [], isLoading } = useChannels();
  const [editing, setEditing] = useState<{ kind: ChannelKind; channel?: Channel } | null>(null);

  return (
    <div>
      <PageHeader
        title="Inbox channels"
        description="Every channel you connect sends its conversations to the inbox, and a stranger's first message to the Incoming column."
        breadcrumbs={[{ label: 'Inbox', to: '/inbox' }, { label: 'Channels' }]}
      />

      <Can permission="inbox.channel.create">
        <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {KINDS.map(({ kind, blurb }) => (
            <button
              key={kind}
              onClick={() => setEditing({ kind })}
              className="group rounded-xl border bg-card p-4 text-start transition-all hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
            >
              <span
                className="flex h-10 w-10 items-center justify-center rounded-lg text-white"
                style={{ backgroundColor: KIND_COLOURS[kind] }}
              >
                {kind === 'WEB_CHAT' ? (
                  <Globe className="h-5 w-5" />
                ) : kind === 'TELEGRAM' ? (
                  <Send className="h-5 w-5" />
                ) : kind === 'EMAIL' ? (
                  <Mail className="h-5 w-5" />
                ) : (
                  <MessageCircle className="h-5 w-5" />
                )}
              </span>
              <p className="mt-3 flex items-center gap-1 font-semibold">
                {KIND_LABELS[kind]}
                <Plus className="ms-auto h-4 w-4 text-muted-foreground group-hover:text-primary" />
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{blurb}</p>
            </button>
          ))}
        </div>
      </Can>

      <p className="mb-2 text-sm text-muted-foreground">
        WhatsApp numbers are connected on the <Link to="/marketing/whatsapp?tab=numbers" className="text-link hover:underline">WhatsApp page</Link> and appear in the same inbox.
      </p>

      {isLoading ? (
        <PageLoader />
      ) : channels.length === 0 ? (
        <p className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
          No channels yet. Start with the website chat — it takes a minute.
        </p>
      ) : (
        <div className="space-y-3">
          {channels.map((channel) => (
            <ChannelRow key={channel.id} channel={channel} onEdit={() => setEditing({ kind: channel.kind, channel })} />
          ))}
        </div>
      )}

      {editing && <ChannelDialog kind={editing.kind} channel={editing.channel} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ChannelRow({ channel, onEdit }: { channel: Channel; onEdit: () => void }) {
  const remove = useDeleteChannel();
  const [confirming, setConfirming] = useState(false);
  const settings = (channel.settings ?? {}) as Record<string, string>;

  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="h-3 w-3 rounded-full" style={{ backgroundColor: KIND_COLOURS[channel.kind] }} />
        <div>
          <p className="font-semibold">{channel.name}</p>
          <p className="text-xs text-muted-foreground">
            {KIND_LABELS[channel.kind]}
            {settings.botUsername ? ` · @${settings.botUsername}` : ''}
            {settings.pageName ? ` · ${settings.pageName}` : ''}
            {channel.mailbox ? ` · ${channel.mailbox.address}` : ''} · {channel.conversationCount} conversations
            {channel.replyWithinMinutes ? ` · reply within ${REPLY_TARGETS.find(([value]) => value === channel.replyWithinMinutes)?.[1] ?? `${channel.replyWithinMinutes} min`}` : ''}
          </p>
        </div>
        <div className="ms-auto flex items-center gap-2">
          {channel.lastError ? (
            <Badge variant="destructive" title={channel.lastError}>
              <AlertTriangle className="me-1 h-3 w-3" /> Problem
            </Badge>
          ) : channel.isActive && channel.connected ? (
            <Badge variant="success">
              <Check className="me-1 h-3 w-3" /> Live
            </Badge>
          ) : (
            <Badge variant="secondary">Off</Badge>
          )}
          {channel.salesbot && <Badge variant="outline">Bot: {channel.salesbot.name}</Badge>}
          <Can permission="inbox.channel.update">
            <Button size="sm" variant="outline" onClick={onEdit}>
              Settings
            </Button>
          </Can>
          <Can permission="inbox.channel.delete">
            {confirming ? (
              <Button size="sm" variant="destructive" onClick={() => remove.mutate(channel.id)}>
                Disconnect?
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </Can>
        </div>
      </div>

      {channel.lastError && <p className="mt-2 text-xs text-destructive">{channel.lastError}</p>}

      {channel.embedCode && (
        <CopyBlock label="Paste this before </body> on every page of your website" value={channel.embedCode} />
      )}
      {channel.webhookUrl && channel.kind === 'SMS' && <CopyBlock label="Twilio → the number → A message comes in (webhook, HTTP POST)" value={channel.webhookUrl} />}
      {channel.webhookUrl && channel.kind !== 'SMS' && (
        <div className="mt-3 grid gap-2 md:grid-cols-2">
          <CopyBlock label="Callback URL (Meta app → Webhooks)" value={channel.webhookUrl} />
          <CopyBlock label="Verify token" value={channel.verifyToken ?? ''} />
        </div>
      )}
    </div>
  );
}

function CopyBlock({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-3">
      <p className="mb-1 text-xs font-medium text-muted-foreground">{label}</p>
      <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2">
        <code className="flex-1 break-all text-xs">{value}</code>
        <button
          onClick={() => {
            void navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          className="shrink-0 text-muted-foreground hover:text-foreground"
          title="Copy"
        >
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function ChannelDialog({ kind, channel, onClose }: { kind: ChannelKind; channel?: Channel; onClose: () => void }) {
  const save = useSaveChannel();
  const { data: users } = useUserOptions();
  const { data: bots } = useSalesbots();
  const settings = (channel?.settings ?? {}) as Record<string, unknown>;

  const [name, setName] = useState(channel?.name ?? (kind === 'WEB_CHAT' ? 'Website chat' : KIND_LABELS[kind]));
  const [token, setToken] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [assignee, setAssignee] = useState(channel?.defaultAssignee?.id ?? '');
  const [botId, setBotId] = useState(channel?.salesbot?.id ?? '');
  const [autoLead, setAutoLead] = useState(channel?.autoCreateLead ?? true);
  const [active, setActive] = useState(channel?.isActive ?? true);
  const [colour, setColour] = useState(String(settings.colour ?? '#1d4ed8'));
  const [smsSid, setSmsSid] = useState(String(settings.accountSid ?? ''));
  const [smsFrom, setSmsFrom] = useState(String(settings.fromNumber ?? ''));
  const [title, setTitle] = useState(String(settings.title ?? 'Chat with us'));
  const [greeting, setGreeting] = useState(String(settings.greeting ?? 'Hi! How can we help you today?'));
  const [position, setPosition] = useState(String(settings.position ?? 'right'));
  const [askName, setAskName] = useState(settings.askName !== false);
  const [askEmail, setAskEmail] = useState(settings.askEmail !== false);
  const [askPhone, setAskPhone] = useState(Boolean(settings.askPhone));
  const [domains, setDomains] = useState(((settings.allowedDomains as string[] | undefined) ?? []).join(', '));
  const [replyWithin, setReplyWithin] = useState(channel?.replyWithinMinutes ? String(channel.replyWithinMinutes) : '');
  const [preset, setPreset] = useState(channel?.mailbox ? 'other' : 'gmail');
  const [box, setBox] = useState({
    address: channel?.mailbox?.address ?? '',
    fromName: channel?.mailbox?.fromName ?? '',
    imapHost: channel?.mailbox?.imapHost ?? MAIL_PRESETS.gmail.imapHost,
    imapPort: String(channel?.mailbox?.imapPort ?? 993),
    smtpHost: channel?.mailbox?.smtpHost ?? MAIL_PRESETS.gmail.smtpHost,
    smtpPort: String(channel?.mailbox?.smtpPort ?? 465),
    username: channel?.mailbox?.username ?? '',
    password: '',
  });
  const setField = (key: keyof typeof box, value: string) => setBox((current) => ({ ...current, [key]: value }));
  const choosePreset = (value: string) => {
    setPreset(value);
    const chosen = MAIL_PRESETS[value];
    setBox((current) => ({
      ...current,
      imapHost: chosen.imapHost,
      imapPort: String(chosen.imapPort),
      smtpHost: chosen.smtpHost,
      smtpPort: String(chosen.smtpPort),
    }));
  };

  const submit = () => {
    save.mutate(
      {
        id: channel?.id,
        ...(channel ? {} : { kind }),
        name,
        ...(token ? { token } : {}),
        ...(appSecret ? { appSecret } : {}),
        defaultAssigneeId: assignee || null,
        salesbotId: botId || null,
        autoCreateLead: autoLead,
        isActive: active,
        replyWithinMinutes: replyWithin ? Number(replyWithin) : null,
        ...(kind === 'EMAIL'
          ? {
              mailbox: {
                address: box.address,
                fromName: box.fromName || undefined,
                imapHost: box.imapHost,
                imapPort: Number(box.imapPort),
                smtpHost: box.smtpHost,
                smtpPort: Number(box.smtpPort),
                username: box.username || box.address,
                ...(box.password ? { password: box.password } : {}),
              },
            }
          : {}),
        ...(kind === 'SMS' ? { settings: { accountSid: smsSid.trim(), fromNumber: smsFrom.trim() } } : {}),
        ...(kind === 'WEB_CHAT'
          ? {
              settings: {
                colour,
                title,
                greeting,
                position,
                askName,
                askEmail,
                askPhone,
                allowedDomains: domains
                  .split(',')
                  .map((item) => item.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, ''))
                  .filter(Boolean),
              },
            }
          : {}),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{channel ? `${channel.name} settings` : `Connect ${KIND_LABELS[kind]}`}</DialogTitle>
          <DialogDescription>
            {kind === 'TELEGRAM' &&
              'In Telegram, open @BotFather, send /newbot, and paste the token it gives you. We point the bot at DovoCRM for you.'}
            {(kind === 'FACEBOOK' || kind === 'INSTAGRAM') &&
              'Create a Meta app with Messenger (or Instagram) enabled, generate a Page access token, and paste it here. After saving, put the callback URL and verify token into the app’s webhook settings and subscribe to "messages".'}
            {kind === 'WEB_CHAT' && 'Choose how the chat looks; after saving you get one line of code for your website.'}
            {kind === 'EMAIL' &&
              'New mail to this address becomes a conversation (and a stranger an Incoming lead); replies go out from the same mailbox. Only mail arriving after you connect is brought in.'}
            {kind === 'VIBER' && 'Create a bot account at partners.viber.com, copy its token and paste it here. We connect the bot to DovoCRM for you.'}
            {kind === 'SMS' &&
              'Enter your Twilio Account SID, auth token and the number or approved sender ID to send from. After saving, paste the webhook URL into the number’s "A message comes in" setting in Twilio.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </div>

          {kind === 'EMAIL' && (
            <div className="grid gap-3 rounded-xl border p-3 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Provider</Label>
                <Select value={preset} onChange={(event) => choosePreset(event.target.value)} className="h-9">
                  <option value="gmail">Gmail / Google Workspace</option>
                  <option value="outlook">Outlook / Microsoft 365</option>
                  <option value="zoho">Zoho Mail</option>
                  <option value="other">Other (enter settings)</option>
                </Select>
                <p className="text-xs text-muted-foreground">{MAIL_PRESETS[preset]?.note}</p>
              </div>
              <div className="space-y-1.5">
                <Label>Email address</Label>
                <Input value={box.address} onChange={(event) => setField('address', event.target.value)} placeholder="sales@yourcompany.ae" />
              </div>
              <div className="space-y-1.5">
                <Label>Sender name</Label>
                <Input value={box.fromName} onChange={(event) => setField('fromName', event.target.value)} placeholder="Acme Sales" />
              </div>
              <div className="space-y-1.5">
                <Label>Username</Label>
                <Input value={box.username} onChange={(event) => setField('username', event.target.value)} placeholder="Usually the address" />
              </div>
              <div className="space-y-1.5">
                <Label>Password / app password</Label>
                <Input
                  type="password"
                  autoComplete="off"
                  value={box.password}
                  onChange={(event) => setField('password', event.target.value)}
                  placeholder={channel ? 'Saved — type to replace' : ''}
                />
              </div>
              {preset === 'other' && (
                <>
                  <div className="space-y-1.5">
                    <Label>IMAP server</Label>
                    <div className="flex gap-2">
                      <Input value={box.imapHost} onChange={(event) => setField('imapHost', event.target.value)} placeholder="imap.example.com" />
                      <Input value={box.imapPort} onChange={(event) => setField('imapPort', event.target.value)} className="w-20" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>SMTP server</Label>
                    <div className="flex gap-2">
                      <Input value={box.smtpHost} onChange={(event) => setField('smtpHost', event.target.value)} placeholder="smtp.example.com" />
                      <Input value={box.smtpPort} onChange={(event) => setField('smtpPort', event.target.value)} className="w-20" />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {kind !== 'WEB_CHAT' && kind !== 'EMAIL' && (
            <div className="space-y-1.5">
              <Label>{kind === 'TELEGRAM' || kind === 'VIBER' ? 'Bot token' : kind === 'SMS' ? 'Twilio auth token' : 'Page access token'}</Label>
              <Input
                value={token}
                onChange={(event) => setToken(event.target.value)}
                placeholder={channel?.connected ? 'Saved — paste a new one to replace it' : kind === 'TELEGRAM' ? '123456789:AA…' : 'EAAG…'}
                type="password"
                autoComplete="off"
              />
            </div>
          )}
          {kind === 'SMS' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Account SID</Label>
                <Input value={smsSid} onChange={(event) => setSmsSid(event.target.value)} placeholder="AC…" />
              </div>
              <div className="space-y-1.5">
                <Label>Send from</Label>
                <Input value={smsFrom} onChange={(event) => setSmsFrom(event.target.value)} placeholder="+9715… or SENDER" />
              </div>
            </div>
          )}
          {(kind === 'FACEBOOK' || kind === 'INSTAGRAM') && (
            <div className="space-y-1.5">
              <Label>App secret (recommended)</Label>
              <Input
                value={appSecret}
                onChange={(event) => setAppSecret(event.target.value)}
                placeholder={channel?.hasAppSecret ? 'Saved — paste a new one to replace it' : 'Used to check that webhooks really come from Meta'}
                type="password"
                autoComplete="off"
              />
            </div>
          )}

          {kind === 'WEB_CHAT' && (
            <div className="grid gap-3 rounded-xl border p-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Colour</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={colour} onChange={(event) => setColour(event.target.value)} className="h-9 w-12 rounded border" />
                  <Input value={colour} onChange={(event) => setColour(event.target.value)} className="h-9" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Position</Label>
                <Select value={position} onChange={(event) => setPosition(event.target.value)} className="h-9">
                  <option value="right">Bottom right</option>
                  <option value="left">Bottom left</option>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Title</Label>
                <Input value={title} onChange={(event) => setTitle(event.target.value)} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Greeting</Label>
                <Textarea value={greeting} onChange={(event) => setGreeting(event.target.value)} rows={2} />
              </div>
              <div className="flex flex-wrap gap-4 text-sm sm:col-span-2">
                <label className="flex items-center gap-2">
                  <Switch checked={askName} onCheckedChange={setAskName} /> Ask name
                </label>
                <label className="flex items-center gap-2">
                  <Switch checked={askEmail} onCheckedChange={setAskEmail} /> Ask email
                </label>
                <label className="flex items-center gap-2">
                  <Switch checked={askPhone} onCheckedChange={setAskPhone} /> Ask phone
                </label>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Only on these sites (optional)</Label>
                <Input value={domains} onChange={(event) => setDomains(event.target.value)} placeholder="example.ae, shop.example.ae" />
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>New conversations go to</Label>
              <Select value={assignee} onChange={(event) => setAssignee(event.target.value)} className="h-9">
                <option value="">Nobody (team picks up)</option>
                {(users ?? []).map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Salesbot that answers first</Label>
              <Select value={botId} onChange={(event) => setBotId(event.target.value)} className="h-9">
                <option value="">None</option>
                {(bots ?? []).map((bot) => (
                  <option key={bot.id} value={bot.id}>
                    {bot.name}
                    {bot.status !== 'ACTIVE' ? ' (off)' : ''}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Reply-time target</Label>
            <Select value={replyWithin} onChange={(event) => setReplyWithin(event.target.value)} className="h-9">
              <option value="">No target</option>
              {REPLY_TARGETS.map(([value, label]) => (
                <option key={value} value={value}>
                  Reply within {label}
                </option>
              ))}
            </Select>
            <p className="text-xs text-muted-foreground">
              A customer waiting longer is marked overdue and the owner and sales managers are told.
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Switch checked={autoLead} onCheckedChange={setAutoLead} />
            Make an Incoming lead from a stranger’s first message
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={active} onCheckedChange={setActive} />
            Channel is on
          </label>
        </div>

        {save.error && <FormError message={(save.error as ApiRequestError).message} />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending || name.trim().length < 2}>
            {save.isPending ? 'Connecting…' : channel ? 'Save' : 'Connect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
